/**
 * Drives an isolated print: hold a job, let the caller render its print
 * document, and when that document reports ready, flag <body> so only the
 * print root reaches paper, call window.print(), then clean up.
 *
 * Cleanup runs on `afterprint` (every supported browser fires it). It must
 * not run straight after window.print(): where print() does not block, that
 * would drop the flag before the page is captured.
 */
import { useCallback, useEffect, useState } from 'react';

export const PRINT_ISOLATE_ATTR = 'printIsolate';

export function useIsolatedPrint<Job>() {
  const [job, setJob] = useState<Job | null>(null);

  const startPrint = useCallback((next: Job) => setJob(next), []);

  const finish = useCallback(() => {
    delete document.body.dataset[PRINT_ISOLATE_ATTR];
    setJob(null);
  }, []);

  const handleReady = useCallback(() => {
    document.body.dataset[PRINT_ISOLATE_ATTR] = 'true';
    const cleanup = () => {
      window.removeEventListener('afterprint', cleanup);
      finish();
    };
    window.addEventListener('afterprint', cleanup);
    window.print();
  }, [finish]);

  // Never leave the page flagged if the owner unmounts mid-print.
  useEffect(() => () => {
    delete document.body.dataset[PRINT_ISOLATE_ATTR];
  }, []);

  return { job, startPrint, handleReady };
}
